import React, { useEffect, useState } from "react";
import { useForm,Controller } from "react-hook-form";
import Modal from "../../../components/form/Modal";
import ControllerInput from "../../../components/form/ControllerInput";
import ControllerSelect from "../../../components/form/ControllerSelect";
import DataTable from "../../../components/form/DataTable";
import FormSection from "../../../components/form/FormSection";
import AlertMessage from "../../../components/form/AlertMessage";
import { getChapters,addChapter,updateChapter,deleteChapter } from "../../../api/Api";
import { getClasses } from "../../../api/dropdownApi";
import { getTermsByClass, getDivisionsByClass, getSubjectsByClass } from "../../../api/dependentDropdownApi";

const ChapterMaster = () => {
  const { control, handleSubmit, reset, watch, setValue } = useForm();
  const [chapters, setChapters] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [classes, setClasses] = useState([]);
  const [terms, setTerms] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const selectedClass = watch("class_id");

  /** Fetch all chapters */
  const fetchChapters = async () => {
    try {
      const response = await getChapters();
      const resData = response.data; // Axios response
      if (resData.success) {
        setChapters(resData.data);
      } else {
        setChapters([]);
      }
    } catch (err) {
      console.error(err);
      setChapters([]);
    }
  };

  /** Fetch all classes */
  const fetchClasses = async () => {
    try {
      const response = await getClasses();
      const resData = response.data;
      if (resData.success) {
        setClasses(resData.data);
      } else {
        setClasses([]);
      }
    } catch (err) {
      console.error(err);
      setClasses([]);
    }
  };

  /** When class changes → fetch dependent dropdowns & reset lower ones */
  useEffect(() => {
    if (selectedClass) {
      Promise.all([
        getTermsByClass(selectedClass),
        getDivisionsByClass(selectedClass),
        getSubjectsByClass(selectedClass),
      ])
        .then(([termRes, divRes, subjRes]) => {
          setTerms(termRes.data.data || []); // Adjust based on your API
          setDivisions(divRes.data.data || []);
          setSubjects(subjRes.data.data || []);

          console.log("Term response:", termRes.data);
          console.log("Terms array:", termRes.data.data);
        })
        .catch(() => {
          setTerms([]);
          setDivisions([]);
          setSubjects([]);
        });

      // reset dependent fields
      setValue("term_id", "");
      setValue("division_id", "");
      setValue("subject_id", "");
    } else {
      setTerms([]);
      setDivisions([]);
      setSubjects([]);
      setValue("term_id", "");
      setValue("division_id", "");
      setValue("subject_id", "");
    }
  }, [selectedClass, setValue]);

  useEffect(() => {
    fetchChapters();
    fetchClasses();
  }, []);

  /** Add or update chapter */
  const onSubmit = async (formData) => {
    try {
      if (editData) await updateChapter(editData.id, formData);
      else await addChapter(formData);
      setShowModal(false);
      reset();
      fetchChapters();
      setSuccessMessage("Chapter Data Saved Successfully.");
    } catch (err) {
      console.error(err);
      setErrorMessage("Failed");
    }
  };

  /** Edit existing record */
  const handleEdit = async (row) => {
    setEditData(row);

    if (row.class_id) {
      try {
        const [termRes, divRes, subjectRes] = await Promise.all([
          getTermsByClass(row.class_id),
          getDivisionsByClass(row.class_id),
          getSubjectsByClass(row.class_id),
        ]);
        setTerms(termRes.data.data || []);
        setDivisions(divRes.data.data || []);
        setSubjects(subjectRes.data.data || []);
      } catch (err) {
        setTerms([]);
        setDivisions([]);
        setSubjects([]);
      }
    }

    reset(row);
    setShowModal(true);
  };

  /** Delete chapter */
  const handleDelete = async (id) => {
    if (window.confirm("Delete this chapter?")) {
      try {
        await deleteChapter(id);
        fetchChapters();
      } catch (err) {
        console.error(err);
      }
    }
  };

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between mb-3">
        <FormSection 
            title={"Chapter Master"}
        />
        <button
          className="btn btn-primary"
          onClick={() => {
            setEditData(null);
            reset();
            setShowModal(true);
          }}
        >
          Add Chapter
        </button>
      </div>
      
      <AlertMessage type="danger" message={errorMessage} onClose={() => setErrorMessage("")} />
      <AlertMessage type="success" message={successMessage} onClose={() => setSuccessMessage("")} />

      <DataTable
        columns={[
          { label: "Term", field: "term" },
          { label: "Class", field: "class" },
          { label: "Division", field: "division" },
          { label: "Subject", field: "subject" },
          { label: "Chapter Name", field: "chapter_name" },
          { label: "Total Lectures", field: "total_lectures" },
          { label: "Actions", field: "action" },
        ]}
        data={chapters.map((ch) => ({
          term: <span>{ch.term || "-"}</span>,
          class: <span>{ch.class || "-"}</span>,
          division: <span>{ch.division || "-"}</span>,
          subject: <span>{ch.subject || "-"}</span>,
          chapter_name: <span>{ch.chapter_name || "-"}</span>,
          total_lectures: <span>{ch.total_lectures || 0}</span>,
          action: (
            <div style={{ display: "flex", gap: "5px" }}>
              <button
                className="btn btn-sm btn-warning"
                onClick={() => handleEdit(ch)}
              >
                Edit
              </button>
              <button
                className="btn btn-sm btn-danger"
                onClick={() => handleDelete(ch.id)}
              >
                Delete
              </button>
            </div>
          ),
        }))}
      />

      <Modal
        show={showModal}
        title={editData ? "Edit Chapter" : "Add Chapter"}
        onClose={() => setShowModal(false)}
        classname={"modal-lg"}
        showBtn={false}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row">
                <div className="col-md-6">
                    <Controller
                        name="class_id"
                        control={control}
                        defaultValue=""
                        rules={{ required: "Class is required" }}
                        render={({ field, fieldState }) => (
                        <ControllerSelect
                            label="Class"
                            options={classes.map((c) => ({
                            label: c.class_name || c.name,
                            value: c.id,
                            }))}
                            {...field}
                            error={fieldState.error?.message}
                        />
                        )}
                    />
                </div>
                <div className="col-md-6">
                    <Controller
                        name="term_id"
                        control={control}
                        defaultValue=""
                        rules={{ required: "Term is required" }}
                        render={({ field, fieldState }) => (
                        <ControllerSelect
                            label="Term"
                            options={terms.map((t) => ({
                            label: t.term || t.name,
                            value: t.id,
                            }))}
                            {...field}
                            error={fieldState.error?.message}
                        />
                        )}
                    />
                </div>
                <div className="col-md-6">
                    <Controller
                        name="division_id"
                        control={control}
                        defaultValue=""
                        rules={{ required: "Division is required" }}
                        render={({ field, fieldState }) => (
                        <ControllerSelect
                            label="Division"
                            options={divisions.map((d) => ({
                            label: d.division || d.name,
                            value: d.id,
                            }))}
                            {...field}
                            error={fieldState.error?.message}
                        />
                        )}
                    />
                </div>
                <div className="col-md-6">
                    <Controller
                        name="subject_id"
                        control={control}
                        defaultValue=""
                        rules={{ required: "Subject is required" }}
                        render={({ field, fieldState }) => (
                        <ControllerSelect
                            label="Subject"
                            options={subjects.map((s) => ({
                            label: s.subject_name || s.name,
                            value: s.id,
                            }))}
                            {...field}
                            error={fieldState.error?.message}
                        />
                        )}
                    />
                </div>
                <div className="col-md-6">
                    <Controller
                        name="chapter_name"
                        control={control}
                        defaultValue=""
                        rules={{ required: "Chapter name is required" }}
                        render={({ field, fieldState }) => (
                        <ControllerInput
                            label="Chapter Name"
                            {...field}
                            error={fieldState.error?.message}
                        />
                        )}
                    />
                </div>
                <div className="col-md-6">
                    <Controller
                        name="total_lectures"
                        control={control}
                        defaultValue=""
                        rules={{
                        required: "Total lectures required",
                        min: { value: 1, message: "Minimum 1 lecture" },
                        }}
                        render={({ field, fieldState }) => (
                        <ControllerInput
                            label="Total Lectures"
                            type="number"
                            {...field}
                            error={fieldState.error?.message}
                        />
                        )}
                    />
                </div>
            </div>

          <div className="mt-3 text-end">
            <button
              className="btn btn-secondary me-2"
              type="button"
              onClick={() => setShowModal(false)}
            >
              Cancel
            </button>
            <button className="btn btn-success" type="submit">
              {editData ? "Update" : "Save"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ChapterMaster;
